/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store/index";
import { RELATED_EMAIL_TYPE } from "@/static/relatedEmailTypes";
import {
  getMailContentAndSubject,
  getMailNotificationStatuses,
  getNotificationData,
  saveMailNotificationStatuses,
  sendMail,
} from "@/requests/mail";
import { NotificationData } from "@/models/api/mail/NotificationData";
import { MAIL_NOTIFICATION_STATUS_EMAIL_TYPE } from "@/static/mailNotificationStatusEmailTypes";
import { DocumentViewModel } from "@/models/api/mail/Document";
import {
  DocumentMailNotificationResponse,
  SaveDocumentMailNotificationStatus,
} from "@/models/api/mail/DocumentMailNotification";
import { EmailAddressType, SendMail } from "@/models/api/mail/SendMail";
import { DocumentToPrint } from "@/models/api/mail/DocumentToPrint";
import { EmailAddress } from "@/models/api/mail/EmailAddress";
import { FileTmp } from "@/models/api/Files";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import { createPreviewDocument } from "@/requests/document";
import { getOpenTemporaryFile } from "@/requests/general";
import _ from "lodash";

export interface MailsStateInterface {
  documentIds: null | Array<string>;
  relatedEmailType: RELATED_EMAIL_TYPE;
  statusRelatedEmailType: RELATED_EMAIL_TYPE;
  notificationData: null | NotificationData;
  emailSubject: null | string;
  emailContent: null | string;
  emailType: null | MAIL_NOTIFICATION_STATUS_EMAIL_TYPE;
  emailAttachments: Array<FileTmp>;
  isAllInOneFile: boolean;
  sendFrom: null | number;
  documentViewModels: Array<DocumentViewModel>;
  isNotificationDialog: boolean;
  isNotificationStatusDialog: boolean;
  notificationStatusData: null | DocumentMailNotificationResponse;
  emailAddressesToSend: Array<EmailAddress>;
  messageForUser: null | string;
}

export const MailsState: MailsStateInterface = {
  documentIds: null,
  relatedEmailType: RELATED_EMAIL_TYPE.Invoice,
  statusRelatedEmailType: RELATED_EMAIL_TYPE.Invoice,
  notificationData: null,
  emailContent: null,
  emailSubject: null,
  emailType: null,
  emailAttachments: [],
  isAllInOneFile: false,
  sendFrom: null,
  documentViewModels: [],
  isNotificationDialog: false,
  isNotificationStatusDialog: false,
  notificationStatusData: null,
  emailAddressesToSend: [],
  messageForUser: null,
};

const MailsModule = defineModule({
  state: MailsState as MailsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setDocumentIds(state, param: Array<string> | null) {
      state.documentIds = param;
    },
    setRelatedEmailType(state, relatedEmailType: RELATED_EMAIL_TYPE) {
      state.relatedEmailType = relatedEmailType;
    },
    setStatusRelatedEmailType(state, relatedEmailType: RELATED_EMAIL_TYPE) {
      state.statusRelatedEmailType = relatedEmailType;
    },
    setNotificationData(state, notificationData: NotificationData | null) {
      state.notificationData = notificationData;
    },
    setEmailContent(state, content: string | null) {
      state.emailContent = content;
    },
    setEmailSubject(state, subject: string | null) {
      state.emailSubject = subject;
    },
    setIsAllInOneFile(state, isAllInOneFile: boolean) {
      state.isAllInOneFile = isAllInOneFile;
    },
    setDocumentViewModels(state, documents: Array<DocumentViewModel>) {
      state.documentViewModels = documents;
    },
    setSendFrom(state, sendFrom: number | null) {
      state.sendFrom = sendFrom;
    },
    setEmailType(state, emailType: MAIL_NOTIFICATION_STATUS_EMAIL_TYPE | null) {
      state.emailType = emailType;
    },
    setIsNotificationDialog(state, param: boolean) {
      state.isNotificationDialog = param;
    },
    setIsNotificationStatusDialog(state, param: boolean) {
      state.isNotificationStatusDialog = param;
    },
    setNotificationStatusData(state, param: DocumentMailNotificationResponse | null) {
      state.notificationStatusData = param;
    },
    setEmailAddressesToSend(state, param: Array<EmailAddress>) {
      state.emailAddressesToSend = param;
    },
    setEmailAttachments(state, param: Array<FileTmp>) {
      state.emailAttachments = param;
    },
    setMessageForUser(state, param: null | string) {
      state.messageForUser = param;
    },
    clear(state) {
      state.notificationData = null;
      state.emailAddressesToSend = [];
      state.sendFrom = null;
      state.emailContent = null;
      state.emailType = null;
      state.emailSubject = null;
      state.emailAttachments = [];
      state.isAllInOneFile = false;
    },
  },
  actions: {
    async openNotificationDialog(context, documentIds: Array<string>) {
      const { commit, dispatch } = _moduleActionContext(context);
      commit.clear();
      commit.setDocumentIds(documentIds);
      commit.setDocumentViewModels([]);
      commit.setRelatedEmailType(RELATED_EMAIL_TYPE.Invoice);
      commit.setIsNotificationDialog(true);
      await dispatch.downloadNotificationData();
    },
    async downloadNotificationData(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.documentIds !== null) {
        if (state.relatedEmailType === RELATED_EMAIL_TYPE.Invoice) {
          commit.setEmailType(MAIL_NOTIFICATION_STATUS_EMAIL_TYPE.InvoiceWithLink);
        } else if (state.relatedEmailType === RELATED_EMAIL_TYPE.Vindication) {
          commit.setEmailType(MAIL_NOTIFICATION_STATUS_EMAIL_TYPE.PaymentReminder);
        }
        const response = await getNotificationData(state.documentIds, state.relatedEmailType);
        commit.setNotificationData(response);
        commit.setEmailSubject(response.mailContentAndSubject.subject);
        commit.setEmailContent(response.mailContentAndSubject.content);
        commit.setMessageForUser(response.documentMailData.messageForUser);
        commit.setDocumentViewModels(
          response.documentMailData.documents.map((v) => new DocumentViewModel(v, response.documentMailData.copyCount))
        );
        if (state.relatedEmailType === RELATED_EMAIL_TYPE.Invoice) {
          commit.setEmailType(
            response.contractorMailSettings.defaultDocumentAccess as unknown as MAIL_NOTIFICATION_STATUS_EMAIL_TYPE
          );
        }
        if (response.mailBoxes.length === 0) {
          commit.setSendFrom(null);
        } else {
          const defaultMailboxId = response.contractorMailSettings.defaultMailbox;
          const mailboxId = response.mailBoxes.find((x) => x.id === defaultMailboxId)?.id ?? response.mailBoxes[0].id;
          commit.setSendFrom(mailboxId as number);
        }
        commit.setEmailAddressesToSend(response.contractorMailSettings.emailAddresses);
      }
    },
    async downloadContentAndSubject(context) {
      const { state, commit } = _moduleActionContext(context);
      const resp = await getMailContentAndSubject({
        allInOneFile: state.isAllInOneFile,
        documents: state.documentViewModels.filter((v) => v.isSelected).map((v) => v.toDocumentToPrint()),
        mailConfigurationId: state.sendFrom,
        emailType: state.emailType as MAIL_NOTIFICATION_STATUS_EMAIL_TYPE,
      });
      commit.setEmailContent(resp.content);
      commit.setEmailSubject(resp.subject);
    },
    async openNotificationStatusDialog(context, documentIds: Array<string>) {
      const { commit, dispatch } = _moduleActionContext(context);
      commit.setNotificationStatusData(null);
      commit.setStatusRelatedEmailType(RELATED_EMAIL_TYPE.Invoice);
      commit.setDocumentIds(documentIds);
      commit.setIsNotificationStatusDialog(true);
      await dispatch.downloadNotificationStatusData();
    },
    async downloadNotificationStatusData(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setNotificationStatusData(null);
      if (state.documentIds !== null && state.documentIds.length > 0) {
        getMailNotificationStatuses(state.documentIds, state.statusRelatedEmailType).then((resp) =>
          commit.setNotificationStatusData(resp)
        );
      }
    },
    async saveNotificationStatusData(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.notificationStatusData !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await saveMailNotificationStatuses(
          state.notificationStatusData.map((v) => SaveDocumentMailNotificationStatus.fromDocumentMailNotification(v)),
          state.statusRelatedEmailType
        );
      }
    },
    async sendMails(context) {
      const { state, commit, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.waitForCompleteAllRequests();
      const sendMailModel = new SendMail();
      sendMailModel.Content = state.emailContent as string;
      sendMailModel.MailConfigurationId = state.sendFrom as number;
      sendMailModel.Subject = state.emailSubject as string;
      sendMailModel.AllInOneFile = state.isAllInOneFile;
      sendMailModel.TemporaryFileIds = state.emailAttachments.map((v) => v.id);
      sendMailModel.EmailType = state.emailType as MAIL_NOTIFICATION_STATUS_EMAIL_TYPE;
      sendMailModel.Documents = state.documentViewModels
        .filter((v) => v.isSelected)
        .map(
          (v) =>
            ({
              id: v.id,
              documentPrintType: v.documentPrintType,
              copyCount: v.copyCount,
            } as DocumentToPrint)
        );
      sendMailModel.EmailAddressesToSend = state.emailAddressesToSend
        .filter((v) => v.isSelected)
        .map(
          (v) =>
            ({
              EmailAddress: v.emailAddress,
              TypeEmailAddress: v.typeEmailAddress,
            } as EmailAddressType)
        );
      sendMailModel.BookingFiles = _.uniqBy(
        state.documentViewModels.filter((v) => v.isSelected).flatMap((v) => v.bookingFiles),
        "id"
      );
      await sendMail(sendMailModel);
      commit.setIsNotificationDialog(false);
      rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("addedToQueue"))));
      await rootDispatch.orders.getOrdersTable();
    },
    async previewDocuments(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const selectedDocuments = state.documentViewModels
        .filter((v) => v.isSelected)
        .map(
          (v) =>
            ({
              id: v.id,
              documentPrintType: v.documentPrintType,
              copyCount: v.copyCount,
            } as DocumentToPrint)
        );
      if (selectedDocuments.length === 0) {
        rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("selectDocumentsFirst")), "error"));
      } else {
        const documentId = await createPreviewDocument(selectedDocuments);
        await getOpenTemporaryFile(documentId);
      }
    },
  },
});

export default MailsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, MailsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, MailsModule);
